import { DateTime } from '@ksyos/date-time';
import { Text } from '@ksyos/design-system';
import { connect } from 'react-redux';
import type { IRequest } from '../../../../../common/interfaces/IRequest';
import type { IReduxState } from '../../../../configureStore';
import type { IOSASWithQuestionnaires } from '../../../pages/OSAS/NewOsasQuestionnaires';
import { OsasQuestionnaireName } from '../../../../../common/enums/OsasQuestionnaireName';
import { RequestStatus } from '../../../../../common/enums/RequestStatus';
import { ServiceCode } from '../../../../../common/enums/ServiceCode';
import { getIsOsasQuestionnaireComplete } from '../../../../utils/getIsOsasQuestionnaireComplete';
import { getLastSomnologyRequest } from '../../../../utils/getLastSomnologyRequest';
import { ReportType } from '../../../pages/OSAS/NewOsasQuestionnaires';
import { CardTheme, FixedCard } from '../../molecules/card/Card';

const mapStateToProps = ({ authentication }: IReduxState): IOsasQuestionnairesCardReduxProps => ({
    processes: authentication.requestProcesses,
});

interface IOsasQuestionnairesCardReduxProps {
    processes?: IRequest[];
}

export function OsasQuestionnairesCardWrapper(props: IOsasQuestionnairesCardReduxProps) {
    if (!props.processes) {
        return null;
    }

    const lastProcess = getLastSomnologyRequest(props.processes);

    if (
        !lastProcess ||
        ![ServiceCode.OSASO, ServiceCode.OSASVO, ServiceCode.OSASC2, ServiceCode.OSASQ].includes(
            lastProcess.service.code as ServiceCode
        ) ||
        (lastProcess.service.code === ServiceCode.OSASO &&
            lastProcess.procedure?.customControlData.numberOfRecordings === 0) ||
        (lastProcess.service.code === ServiceCode.OSASVO &&
            lastProcess.procedure?.customControlData.numberOfRecordings === 3)
    ) {
        return null;
    }

    const isFollowUpExamination = [ServiceCode.OSASVO, ServiceCode.OSASQ].includes(
        lastProcess.service.code as ServiceCode
    );

    const osasProcessWithQuestionnaires: IOSASWithQuestionnaires | undefined = props.processes?.find(
        (process) =>
            [ServiceCode.OSASQ, ServiceCode.OSASO, ServiceCode.OSASVO].includes(process.service.code as ServiceCode) &&
            [RequestStatus.active, RequestStatus.completed].includes(process.status)
    );

    const hideQuestionnaireCardForOsasoOsasvoAfterDate = (window as any).ksyosSettings
        .hideQuestionnaireCardForOsasoOsasvoAfterDate;

    if (
        !osasProcessWithQuestionnaires?.procedure ||
        (hideQuestionnaireCardForOsasoOsasvoAfterDate &&
            osasProcessWithQuestionnaires.dateSent &&
            osasProcessWithQuestionnaires.service.code !== ServiceCode.OSASQ &&
            DateTime.from(osasProcessWithQuestionnaires.dateSent).isAfter(
                DateTime.from(hideQuestionnaireCardForOsasoOsasvoAfterDate)
            ))
    ) {
        return null;
    }

    const numberOfSuccessfulRecordings = osasProcessWithQuestionnaires.procedure.customControlData
        ?.nightlyReportMetadata
        ? Object.values(osasProcessWithQuestionnaires.procedure.customControlData.nightlyReportMetadata).filter(
              (report) => report.type === ReportType.diagnostic
          ).length
        : 0;

    function getLastDateSent() {
        return osasProcessWithQuestionnaires?.procedure?.customControlData?.questionnaires?.reduce((a, b) =>
            a.dateSent && b.dateSent && new Date(a.dateSent) > new Date(b.dateSent) ? a : b
        ).dateSent;
    }

    function getAreAllQuestionnairesSent() {
        if (!osasQuestionnaires) {
            return false;
        }

        const isEssSent = getIsOsasQuestionnaireComplete(OsasQuestionnaireName.ess, osasQuestionnaires);
        const isFosq10Sent = getIsOsasQuestionnaireComplete(OsasQuestionnaireName.fosq10, osasQuestionnaires);
        let isHsdqSent = true;

        if (
            (window as any).ksyosSettings.hsdqQuestionnaireEnabled &&
            (!isFollowUpExamination ||
                osasProcessWithQuestionnaires?.customControlData.isHsdqQuestionnaireSentToThePatient)
        ) {
            isHsdqSent = getIsOsasQuestionnaireComplete(OsasQuestionnaireName.hsdq, osasQuestionnaires);
        }

        return isEssSent && isFosq10Sent && isHsdqSent;
    }

    let title;
    let body;
    let linkText;
    let linkTo;

    const osasQuestionnaires = osasProcessWithQuestionnaires.procedure.customControlData?.questionnaires;
    const procedureId = osasProcessWithQuestionnaires.procedure.id;
    const areAllQuestionnairesSent = getAreAllQuestionnairesSent();
    const lastDateSent = getLastDateSent();

    if (!areAllQuestionnairesSent) {
        title = 'Vragen van je slaaparts';
        body =
            'Je slaaparts heeft wat vragen over je slaapklachten. Vul de vragenlijsten in om je klachten te beschrijven.';
        linkText = 'Beantwoord de vragen';
        linkTo = procedureId
            ? `/${osasProcessWithQuestionnaires.service.code
                  .toString()
                  .toLowerCase()}/questionnaires?procedureId=${procedureId}`
            : undefined;
    } else {
        title = 'Je antwoorden zijn verstuurd';
        body = lastDateSent ? `Ontvangen op ${DateTime.from(lastDateSent).format('DD-MM-YYYY')}` : '';
        linkText = undefined;
        linkTo = undefined;
    }

    if (isFollowUpExamination && numberOfSuccessfulRecordings === 3) {
        return null;
    } else {
        return (
            <FixedCard
                body={body}
                cardTitle={title}
                linksText={linkText ? [linkText] : undefined}
                linksTo={linkTo ? [linkTo] : undefined}
                theme={CardTheme.white}
            >
                {areAllQuestionnairesSent && (
                    <Text>We nemen contact met je op om een afspraak met de slaaparts in te plannen.</Text>
                )}
            </FixedCard>
        );
    }
}

export const OsasQuestionnairesCard = connect(mapStateToProps)(OsasQuestionnairesCardWrapper);
